import React, { useContext, useMemo, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { useAppDispatch, useAppSelector } from "@/hooks/reduxToolkitHook"
import { transactionsCreditCustomer } from "@/redux/features/transactionsSlice"
import { ActionNotificationContext, ActionNotificationContextValeurType } from "@/context/ActionNotificationContext"
import ModalForm from "components/common/modal/ModalForm"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Dropzone, DropzoneContent, DropzoneEmptyState } from "@/components/ui/dropzone"
import { getFilePath } from "@/utils/utils"
import {
  Banknote,
  Building,
  Building2,
  Check,
  CheckCircle2,
  ChevronsUpDown,
  CreditCard,
  Info,
  Search,
  X,
} from "lucide-react"
import { cn } from "@/utils/shadcn-utils"

/* =======================
   TYPES
======================= */

type TransactionType = "MANAGER_DEPOSIT" | "MANAGER_REFUND";
type PaymentMethod = "BANK_TRANSFER" | "CHECK" | "CASH";

interface CreditFormData {
  company_id: string;
  transaction_type: TransactionType;
  payment_method: PaymentMethod;
  amount: string;
  motif: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  companyId?: string | null;
}

/* =======================
   CONSTANTS
======================= */

const TRANSACTION_TYPE_OPTIONS = [
  {
    value: "MANAGER_DEPOSIT",
    label: "Dépôt du client",
    description: "Crédit suite à un dépôt effectué par le client",
  },
  {
    value: "MANAGER_REFUND",
    label: "Régularisation - remboursement",
    description: "Crédit suite à une régularisation ou un remboursement",
  },
]

const PAYMENT_METHOD_OPTIONS = [
  { value: "BANK_TRANSFER", label: "Virement bancaire", icon: Building2 },
  { value: "CHECK", label: "Chèque", icon: CreditCard },
  { value: "CASH", label: "Espèces", icon: Banknote },
]

const ACCEPTED_FILE_TYPES = {
  "application/pdf": [".pdf"],
  "image/png": [".png"],
  "image/jpeg": [".jpg", ".jpeg"],
}

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB

/* =======================
   COMPONENT
======================= */

const CreditClientForm = ({ open, onClose, companyId }: Props) => {
  const dispatch = useAppDispatch()
  const { companies } = useAppSelector((state) => state.companies)
  const { displayAlert } = useContext(
    ActionNotificationContext,
  ) as ActionNotificationContextValeurType

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [proofFile, setProofFile] = useState<File | null>(null)
  const [fileError, setFileError] = useState<string>("")
  const [companyPopoverOpen, setCompanyPopoverOpen] = useState(false)

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm<CreditFormData>({
    defaultValues: {
      company_id: companyId || "",
      transaction_type: "MANAGER_DEPOSIT",
      payment_method: "BANK_TRANSFER",
      amount: "",
      motif: "",
    },
  })

  const transactionType = watch("transaction_type")
  const selectedCompanyId = watch("company_id")
  const isDeposit = transactionType === "MANAGER_DEPOSIT"

  const selectedCompany = useMemo(() => {
    return companies.find((c) => c.id === selectedCompanyId)
  }, [companies, selectedCompanyId])

  // File handlers
  const handleFileDrop = (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setProofFile(acceptedFiles[0])
      setFileError("")
    }
  }

  const handleFileError = (error: Error) => {
    let errorMessage = ""
    if (error.message.includes("File type must be")) {
      errorMessage = "Format de fichier non supporté. Veuillez utiliser un fichier PDF ou PNG."
    } else if (error.message.includes("File is larger than")) {
      errorMessage = "Le fichier est trop volumineux. La taille maximale autorisée est de 10MB."
    } else {
      errorMessage = error.message
    }
    setFileError(errorMessage)
    setProofFile(null)
  }

  const removeFile = () => {
    setProofFile(null)
    setFileError("")
  }

  // Form submission
  const onSubmit = async (data: CreditFormData) => {
    if (isDeposit && !proofFile) {
      setFileError("La preuve du dépôt est requise")
      return
    }

    setIsSubmitting(true)

    const formData = new FormData()
    formData.append("company", data.company_id)
    formData.append("amount", data.amount)
    formData.append("payment_method", data.payment_method)
    formData.append("transaction_type", data.transaction_type)
    formData.append("motif", data.motif)

    if (proofFile) {
      formData.append("proof_file", proofFile)
    }

    try {
      await dispatch(transactionsCreditCustomer(formData)).unwrap()
      displayAlert("Transaction créée avec succès", "success")
      handleClose()
    } catch (error) {
      displayAlert("La transaction n'a pas pu être créée", "error")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    reset({
      company_id: companyId || "",
      transaction_type: "MANAGER_DEPOSIT",
      payment_method: "BANK_TRANSFER",
      amount: "",
      motif: "",
    })
    setProofFile(null)
    setFileError("")
    onClose()
  }

  return (
    <ModalForm
      open={open}
      onClose={handleClose}
      formSubmit={handleSubmit(onSubmit)}
      title={`Recharger manuellement ${companyId ? "ce" : "un"} client`}
      className="sm:max-w-[710px]"
      submitButtonProps={{
        isLoading: isSubmitting,
        isDisabled: isSubmitting,
        label: isSubmitting ? "Traitement..." : "Valider",
      }}>
      <div className="space-y-5">
        {/* Company Select - Only if companyId is not provided */}
        {!companyId && (
          <div className="space-y-2">
            <Label>
              Client <span className="text-red-500">*</span>
            </Label>
            <Controller
              name="company_id"
              control={control}
              rules={{ required: "Le client est requis" }}
              render={({ field }) => (
                <Popover open={companyPopoverOpen} onOpenChange={setCompanyPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={companyPopoverOpen}
                      className={cn(
                        "w-full justify-between h-10 font-normal",
                        errors.company_id && "border-red-500",
                      )}>
                      {selectedCompany ? (
                        <div className="flex items-center gap-2">
                          <Building className="h-4 w-4 text-muted-foreground" />
                          <span>{selectedCompany.name}</span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">
                          Rechercher un client...
                        </span>
                      )}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0" style={{ width: "var(--radix-popover-trigger-width)" }}>
                    <Command>
                      <CommandInput placeholder="Rechercher un client..." />
                      <CommandList>
                        <CommandEmpty>
                          <div className="py-6 text-center text-sm">
                            <Search className="mx-auto h-6 w-6 text-muted-foreground mb-2" />
                            <p>Aucun client trouvé</p>
                          </div>
                        </CommandEmpty>
                        <CommandGroup>
                          {companies.map((company) => (
                            <React.Fragment key={company.id}>
                              <CommandItem
                                value={company.name}
                                onSelect={() => {
                                  field.onChange(company.id)
                                  setCompanyPopoverOpen(false)
                                }}
                                className="cursor-pointer">
                                <div className="flex items-center gap-3 w-full">
                                  <Building className="h-4 w-4 text-muted-foreground" />
                                  <span className="flex-1">{company.name}</span>
                                  {field.value === company.id && (
                                    <Check className="h-4 w-4 text-primary" />
                                  )}
                                </div>
                              </CommandItem>
                            </React.Fragment>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              )}
            />
            {errors.company_id && (
              <p className="text-sm text-red-500">{errors.company_id.message}</p>
            )}
          </div>
        )}

        {/* Transaction Type */}
        <div className="space-y-2">
          <Label>
            Motif de l'opération <span className="text-red-500">*</span>
          </Label>
          <Controller
            name="transaction_type"
            control={control}
            rules={{ required: "Le motif est requis" }}
            render={({ field }) => {
              const selectedOption = TRANSACTION_TYPE_OPTIONS.find(
                (opt) => opt.value === field.value,
              )
              return (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger className={errors.transaction_type ? "border-red-500" : ""}>
                    <SelectValue placeholder="Sélectionner un motif">
                      {selectedOption?.label}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {TRANSACTION_TYPE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex flex-col items-start">
                          <span>{option.label}</span>
                          <span className="text-xs text-muted-foreground">
                    {option.description}
                  </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )
            }}
          />
          {errors.transaction_type && (
            <p className="text-sm text-red-500">{errors.transaction_type.message}</p>
          )}
        </div>

        {/* Payment Method */}
        <div className="space-y-2">
          <Label>
            Moyen du dépôt <span className="text-red-500">*</span>
          </Label>
          <Controller
            name="payment_method"
            control={control}
            rules={{ required: "Le moyen de paiement est requis" }}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className={errors.payment_method ? "border-red-500" : ""}>
                  <SelectValue placeholder="Sélectionner un moyen de paiement" />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHOD_OPTIONS.map((option) => {
                    const Icon = option.icon
                    return (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4 text-muted-foreground" />
                          <span>{option.label}</span>
                        </div>
                      </SelectItem>
                    )
                  })}
                </SelectContent>
              </Select>
            )}
          />
          {errors.payment_method && (
            <p className="text-sm text-red-500">{errors.payment_method.message}</p>
          )}
        </div>

        {/* Amount */}
        <div className="space-y-2">
          <Label>
            Montant (F CFA) <span className="text-red-500">*</span>
          </Label>
          <Input
            type="number"
            placeholder="100 000"
            {...register("amount", {
              required: "Le montant est requis",
              min: { value: 1, message: "Le montant doit être supérieur à 0" },
            })}
            className={errors.amount ? "border-red-500" : ""}
          />
          {errors.amount && (
            <p className="text-sm text-red-500">{errors.amount.message}</p>
          )}
        </div>

        {/* Motif */}
        <div className="space-y-2">
          <Label>
            Description ou commentaire <span className="text-red-500">*</span>
          </Label>
          <Textarea
            placeholder="Votre commentaire"
            rows={3}
            {...register("motif", {
              required: "La description est requise",
            })}
            className={errors.motif ? "border-red-500" : ""}
          />
          {errors.motif && (
            <p className="text-sm text-red-500">{errors.motif.message}</p>
          )}
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <Info className="h-3 w-3" />
            Ce commentaire sera visible pour le client
          </p>
        </div>

        {/* File Upload - Only for deposit */}
        {isDeposit && (
          <div className="space-y-2">
            <Label>
              Ajouter la preuve du dépôt <span className="text-red-500">*</span>
            </Label>

            <Dropzone
              accept={ACCEPTED_FILE_TYPES}
              maxFiles={1}
              maxSize={MAX_FILE_SIZE}
              onDrop={handleFileDrop}
              onError={handleFileError}
              className={cn(
                "w-full flex flex-col items-center justify-center gap-4 bg-gray-50 border-2 border-dashed rounded-lg py-6 px-6 transition-colors",
                fileError
                  ? "border-red-500 bg-red-50"
                  : proofFile
                    ? "border-green-500 bg-green-50"
                    : "border-blue-300 hover:border-blue-400",
              )}
              src={proofFile ? [proofFile] : undefined}>
              <DropzoneEmptyState>
                <div className="flex items-center gap-5">
                  <img src={getFilePath("pdf-group.png")} alt="pdf" className="h-14 w-auto" />
                  <div className="text-center space-y-1">
                    <p className="text-sm text-gray-600">
                      Glissez et déposez votre document ici
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Formats supportés : PNG, PDF | Taille max : 10MB
                    </p>
                    <Button type="button" variant="default" size="sm" className="mt-2">
                      Sélectionner un document
                    </Button>
                  </div>
                </div>
              </DropzoneEmptyState>

              <DropzoneContent>
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{proofFile?.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {proofFile?.size && `${(proofFile.size / 1024 / 1024).toFixed(2)} MB`}
                      </p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={removeFile}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50">
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </DropzoneContent>
            </Dropzone>

            {fileError && <p className="text-sm text-red-500">{fileError}</p>}
          </div>
        )}

        {/* Info Alert for Regularisation */}
        {!isDeposit && (
          <div className="p-3 rounded-lg bg-blue-100 border border-blue-300">
            <div className="flex gap-3">
              <Info className="h-5 w-5 text-amber-600 flex-shrink-0" />
              <p className="text-sm text-blue-700">
                Pour une régularisation, la preuve de dépôt n'est pas requise.
              </p>
            </div>
          </div>
        )}
      </div>
    </ModalForm>
  )
}

export default CreditClientForm